import styles from './transformStockForm.module.css'
import { useEffect, useMemo, useState } from "react"
import { PanelWrapper, FieldType, Form, FormField, useUIStateActions, useGetCrumbs, FieldValue, O, option, InputGroup } from "../../../../features/ui"
import { Lot, Product, ProductSelectField, ProductType, useLotLoaders, useProductHooks, useProductLoaders } from "../../../products"
import { useNavigate } from 'react-router-dom'
import { RoutePath, useRoutesHooks } from '../../../../features/routes'
import { define, isDefined, round } from "../../../../utils/typeUtils"
import { Stock } from "../../state/types"
import { TableName } from "../../../../tables"
import { useManagerHooks } from "../../../manager"
import { useStockHooks } from "../../hooks/stockHooks"
import { useUnitTypeLoaders } from "../../loaders/unitTypeLoaders"
import { useActions } from './actions'

export const TransformStockForm = () => {
    const { dataType } = useRoutesHooks().parseUrl()
    const stock = define(dataType) as Stock

    const [sourceProducts, setSourceProducts] = useState<Product[]>([])
    const [defaultProduct, setDefaultProduct] = useState<Product>()
    const [sourceProduct, setSourceProduct] = useState<Product>()
    const [sourceStock, setSourceStock] = useState<number>()
    const [destinationProducts, setDestinationProducts] = useState<Product[]>([])
    const [destinationProduct, setDestinationProduct] = useState<Product>()
    const [destinationLots, setDestinationLots] = useState<Lot[]>([])
    const [destinationLot, setDestinationLot] = useState<Lot>()
    const [sourceQty, setSourceQty] = useState<O<number>>(1)
    const [sourceUnits, setSourceUnits] = useState<O<number>>(1)
    const [destinationQty, setDestinationQty] = useState<O<number>>(1)
    const [destinationUnits, setDestinationUnits] = useState<O<number>>(1)
    const [equivalence, setEquivalence] = useState<O<number>>(0)
    const [sourceQtyLastUpdate, setSourceQtyLastUpdate] = useState<number>(0)
    const [destinationQtyLastUpdate, setDestinationQtyLastUpdate] = useState<number>(0)
    const [destinationKey, setDestinationKey] = useState<number>(0)
    const [loaded, setLoaded] = useState(false)

    const { currentBranchId } = useManagerHooks()
    const { availableStock } = useStockHooks()
    const { describeQuantity, describeUnitType } = useProductHooks()
    const navigate = useNavigate()

    const productLoaders = useProductLoaders()
    const unitTypeLoaders = useUnitTypeLoaders()
    const lotLoaders = useLotLoaders()

    const { submitTransformStock } = useActions()
    const { setDisableBranchSelector } = useUIStateActions()
    
    const load = async () => {
        setDisableBranchSelector(true)
        const { loadTransformableProducts, loadProduct } = productLoaders()
        const transformableProducts = await loadTransformableProducts()
        setSourceProducts(transformableProducts)
        const loadedProduct = await loadProduct(stock.productId)
        setDefaultProduct(loadedProduct)
        setSourceProduct(loadedProduct)
    }

    useEffect(() => {
        load().then(() => setLoaded(true))
        return () => {
            setDisableBranchSelector(false)
        }
    }, [])

    useEffect(() => {
        const equivalenceProductIds = sourceProduct?.equivalences?.map(equivalence => equivalence.productId) || []
        productLoaders().loadProducts(equivalenceProductIds).then(setDestinationProducts)
        sourceProduct && availableStock(sourceProduct, define(currentBranchId)).then(setSourceStock)
        setDestinationKey(destinationKey + 1)
    }, [sourceProduct])

    useEffect(() => {
        destinationProduct && lotLoaders().loadProductLots(destinationProduct.id).then(setDestinationLots)
        !destinationProduct?.loted && setDestinationLot(undefined)
    }, [destinationProduct])

    const definedNumber = (n?: number, defaultN = 0) => n || defaultN

    useEffect(() => {
        const newEquivalence = definedNumber(sourceProduct?.equivalences?.find(equivalence => equivalence.productId === destinationProduct?.id)?.equivalence)
        if (newEquivalence !== equivalence) {
            setEquivalence(newEquivalence)
        }
    }, [sourceProduct, destinationProduct])

    useEffect(() => {
        const newSourceUnits = definedNumber(sourceQty) * (isBulk(sourceProduct) ? 1 : definedNumber(sourceProduct?.units))
        const now = new Date().getTime()
        if (newSourceUnits !== sourceUnits && now > sourceQtyLastUpdate + 100) {
            setSourceUnits(newSourceUnits)
            setSourceQtyLastUpdate(now)
        }
    }, [sourceQty, sourceProduct?.units])

    useEffect(() => {
        const newDestinationUnits = definedNumber(sourceUnits) * definedNumber(equivalence)
        if (newDestinationUnits !== destinationUnits) {
            setDestinationUnits(newDestinationUnits)
        }
    }, [sourceUnits, equivalence])

    useEffect(() => {
        const newDestinationQty = definedNumber(destinationUnits) / definedNumber(destinationProduct?.units, 1)
        const now = new Date().getTime()
        if (newDestinationQty !== destinationQty && now > destinationQtyLastUpdate + 100) {
            setDestinationQty(newDestinationQty)
            setDestinationQtyLastUpdate(now)
        }
    }, [destinationUnits])

    useEffect(() => {
        const newDestinationUnits = definedNumber(destinationQty) * (isBulk(destinationProduct) ? 1 : definedNumber(destinationProduct?.units))
        const newSourceUnits = definedNumber(newDestinationUnits) / (equivalence || 1)
        const newSourceQty = round(definedNumber(newSourceUnits) / definedNumber(sourceProduct?.units, 1))
        if (newSourceQty !== sourceQty) {
            setSourceQty(newSourceQty)
        }
    }, [destinationQty])

    const crumbs = useGetCrumbs(
        'Stock',
        RoutePath.STOCK,
        defaultProduct?.name,
        stock.id,
        'Transformar'
    )

    const isBulk = (product?: Product) => product?.type === ProductType.BULK

    const lotOptions = useMemo(() => destinationLots.map(lot => option(lot.id, lot.code)), [destinationLots])
    const sourceIsBulk = useMemo(() => isBulk(sourceProduct), [sourceProduct?.type])
    const destinationIsBulk = useMemo(() => isBulk(destinationProduct), [destinationProduct?.type])

    const onChangeSourceProduct = (value?: FieldValue) => {
        const newSourceProduct = sourceProducts.find(product => product.id === value)
        setSourceProduct(newSourceProduct)
    }

    const onChangeDestinationProduct = (value?: FieldValue) => {
        const newDestinationProduct = destinationProducts.find(product => product.id === value)
        setDestinationProduct(newDestinationProduct)
    }

    const onChangeDestinationLot = (value?: FieldValue) => {
        const newDestinationLot = destinationLots.find(lot => lot.id === value)
        setDestinationLot(newDestinationLot)
    }

    const onChangeSourceQty = (value?: FieldValue) => {
        const newSourceQty = value as O<number>
        if (newSourceQty !== sourceQty) {
            setSourceQty(newSourceQty)
        }
    }

    const onChangeDestinationQty = (value?: FieldValue) => {
        const newDestinationQty = value as O<number>
        if (newDestinationQty !== destinationQty) {
            setDestinationQty(newDestinationQty)
            setDestinationQtyLastUpdate(new Date().getTime())
        }
    }

    const fields: FormField[] = [
        {
            name: 'sourceProductId',
            type: FieldType.TABLE,
            table: TableName.PRODUCTS,
            label: 'Producto Inicial',
            loading: !loaded,
            render: () => <ProductSelectField
                productList={sourceProducts}
                branchId={currentBranchId}
                defaultValue={defaultProduct?.id}
                focus
                onChange={onChangeSourceProduct}
            />
        },
        {
            name: 'sourceStock',
            type: FieldType.NUMBER,
            label: 'Stock Disponible',
            loading: !loaded,
            render: () => <>{isDefined(sourceStock) && sourceProduct ? describeQuantity(sourceStock!, sourceProduct) : '-'}</>
        },
        {
            name: 'destinationProductId',
            type: FieldType.TABLE,
            table: TableName.PRODUCTS,
            label: 'Producto Final',
            loading: !loaded,
            render: () => <ProductSelectField
                key={destinationKey}
                productList={destinationProducts}
                branchId={currentBranchId}
                onChange={onChangeDestinationProduct}
            />
        },
        {
            name: 'quantity',
            type: FieldType.NUMBER,
            label: 'Cantidad a Transformar',
            loading: !loaded,
            render: () => {
                const { findUnitType } = unitTypeLoaders()
                const sourceUnitType = findUnitType(sourceProduct?.unitTypeId)
                const destinationUnitType = findUnitType(destinationProduct?.unitTypeId)
                const sourceQtyLabel = sourceProduct ? describeUnitType(sourceProduct, true, true) : ''
                return (
                    <div className={styles.qtiesContainer}>
                        <div className={styles.qtyContainer}>
                            <InputGroup
                                id="sourceQty"
                                value={sourceQty}
                                postLabel={sourceQtyLabel}
                                min={sourceIsBulk ? 0.01 : 1}
                                max={sourceStock}
                                integer={!sourceIsBulk}
                                fit
                                className={`${styles.qty} ${sourceQtyLabel.length > 2 && styles.wide}`}
                                onChange={onChangeSourceQty}
                            />
                            {!sourceIsBulk && <>
                                <span className={styles.equals}>=</span>
                                <span className={styles.units}>
                                    {round(definedNumber(sourceUnits))} {sourceUnitType?.abbreviation}
                                </span>
                            </>}
                        </div>
                        <div className={styles.qtyContainer}>
                            <span className={styles.arrow}>→</span>
                            {!destinationIsBulk && sourceUnitType?.id !== destinationUnitType?.id && (
                                <>
                                    <span className={styles.units}>
                                        {round(definedNumber(destinationUnits))} {destinationUnitType?.abbreviation}
                                    </span>
                                    <span className={styles.equals}>=</span>
                                </>
                            )}
                            <InputGroup
                                id="destinationQty"
                                value={destinationQty}
                                postLabel={destinationIsBulk ? destinationUnitType?.abbreviation || '?' : 'u'}
                                min={destinationIsBulk ? 0.01 : 1}
                                integer={!destinationIsBulk}
                                fit
                                className={styles.qty}
                                onChange={onChangeDestinationQty}
                            />
                        </div>
                    </div>
                )
            }
        }
    ]
    if (destinationLots.length > 1) {
        const lotField = {
            name: 'destinationLot',
            type: FieldType.TABLE,
            table: TableName.LOTS,
            label: 'Partida',
            options: lotOptions,
            onChange: onChangeDestinationLot
        }
        fields.splice(3, 0, lotField)
    }

    const onSubmit = () => {
        submitTransformStock(
            define(sourceProduct).id,
            define(destinationProduct).id,
            define(currentBranchId),
            define(sourceQty),
            define(destinationQty),
            destinationLot?.id
        )
    }

    const onCancel = () => navigate(-1)

    return (
        <PanelWrapper
            title="Transformar Stock"
            crumbs={crumbs}
        >
            <Form
                formId="transform-stock-form"
                fields={fields}
                createLabel="Transformar"
                disableSubmit={!loaded}
                onSubmit={onSubmit}
                onCancel={onCancel}
            />
        </PanelWrapper>
    )
}
